import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import {
  Container,
  Header,
  Title,
  Subtitle,
  Section,
  SectionTitle,
  ContactForm,
  Input,
  TextArea,
  Button,
  FAQItem,
  FAQQuestion,
  FAQAnswer,
  ScreenshotGallery,
  ScreenshotPlaceholder,
  FeatureList,
  FeatureCard,
  Footer,
  Link,
  LogoImage
} from './SharedStyledComponents';

const BeestmodeSupportPageEN = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleFAQToggle = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      alert('Please fill in all required fields.');
      return;
    }

    setIsLoading(true);

    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        from_phone: 'BEESTMODE-EN', // Phone field not included in this form
        message: formData.message,
        to_email: 'haggaimagen87@gmail.com'
      };

      await emailjs.send(
        'service_w3pkjf6',      // Service ID
        'template_lwf20nq',     // Template ID
        templateParams,
        'Sgy1Do0paUmA8QyPR'     // Public key
      );

      alert(`Thank you ${formData.name}! Your message has been sent successfully. We'll get back to you soon.`);
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('EmailJS Error:', error);
      alert('Error sending message. Please try again or contact us by phone.');
    } finally {
      setIsLoading(false);
    }
  };

  const faqData = [
    {
      question: "How do I get started with the app?",
      answer: "After downloading the app, open it and create a new account. Follow the on-screen instructions to set up your profile."
    },
    {
      question: "The app is not working - what should I do?",
      answer: "Try closing the app and reopening it. If the problem persists, you can try deleting and reinstalling the app."
    },
    {
      question: "How do I update my personal information?",
      answer: "Go to the app settings, select 'Profile' and there you can edit all your personal details."
    },
    {
      question: "How can I cancel my subscription?",
      answer: "Go to iPhone Settings > iTunes & App Store > Apple ID > View Apple ID > Subscriptions, and choose to cancel the subscription."
    },
    {
      question: "Which iOS versions does the app support?",
      answer: "The app supports iOS 14.0 and above. We recommend updating to the latest operating system."
    }
  ];

  return (
    <Container style={{ direction: 'ltr', textAlign: 'left' }}>
      <Header>
        <LogoImage 
            src="/logo_shade_transparent.png" 
            alt="Fitness App"
          />
        <Title>Beestmode</Title>
        <Subtitle>Build custom workout plans, track performance and get advanced insights</Subtitle>
        <Subtitle>Track performance and get advanced insights</Subtitle>
        <Subtitle>Made in Israel!</Subtitle>
      </Header>

      <Section>
        <SectionTitle>Contact Us</SectionTitle>
        <p>Have questions or issues? We're here to help!</p>
        <p><strong>Email:</strong> haggaimagen87@gmail.com</p>
        <p><strong>Response Hours:</strong> Sunday - Thursday, 9:00 AM - 5:00 PM</p>
        
        <ContactForm onSubmit={handleSubmit}>
          <Input
            type="text"
            name="name"
            placeholder="Full Name"
            value={formData.name}
            onChange={handleInputChange}
            disabled={isLoading}
            required
          />
          <Input
            type="email"
            name="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={handleInputChange}
            disabled={isLoading}
            required
          />
          <TextArea
            name="message"
            placeholder="Describe your issue or question..."
            value={formData.message}
            onChange={handleInputChange}
            disabled={isLoading}
            required
          />
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Sending Message...' : 'Send Message'}
          </Button>
        </ContactForm>
      </Section>

      <Section>
        <SectionTitle>Frequently Asked Questions</SectionTitle>
        {faqData.map((item, index) => (
          <FAQItem key={index}>
            <FAQQuestion onClick={() => handleFAQToggle(index)}>
              {item.question} {openFAQ === index ? '−' : '+'}
            </FAQQuestion>
            {openFAQ === index && (
              <FAQAnswer>{item.answer}</FAQAnswer>
            )}
          </FAQItem>
        ))}
      </Section>

      <Section>
        <SectionTitle>Screenshots</SectionTitle>
        <ScreenshotGallery>
          <ScreenshotPlaceholder>Screenshot 1</ScreenshotPlaceholder>
          <ScreenshotPlaceholder>Screenshot 2</ScreenshotPlaceholder>
          <ScreenshotPlaceholder>Screenshot 3</ScreenshotPlaceholder>
          <ScreenshotPlaceholder>Screenshot 4</ScreenshotPlaceholder>
        </ScreenshotGallery>
      </Section>

      <Section>
        <SectionTitle>Key Features</SectionTitle>
        <FeatureList>
          <FeatureCard>
            <h3>Custom Workout Plans</h3>
            <p>Create personalized workout routines tailored to your fitness goals and experience level.</p>
          </FeatureCard>
          <FeatureCard>
            <h3>Performance Tracking</h3>
            <p>Monitor your progress with detailed analytics and performance metrics over time.</p>
          </FeatureCard>
          <FeatureCard>
            <h3>Exercise Library</h3>
            <p>Access a comprehensive database of exercises with instructions and video demonstrations.</p>
          </FeatureCard>
          <FeatureCard>
            <h3>Advanced Insights</h3>
            <p>Get intelligent recommendations and insights to optimize your training effectiveness.</p>
          </FeatureCard>
        </FeatureList>
      </Section>

      <Section>
        <SectionTitle>App Information</SectionTitle>
        <p><strong>Current Version:</strong> 1.0.0</p>
        <p><strong>Compatibility:</strong> iOS 14.0 and above</p>
        <p><strong>Size:</strong> 45.2 MB</p>
        <p><strong>Last Update:</strong> July 15, 2025</p>
        <p><strong>Category:</strong> Health & Fitness</p>
        <p><strong>Developer:</strong> Fitness Solutions</p>
      </Section>

      <Section>
        <SectionTitle>Additional Support</SectionTitle>
        <p>For more detailed help, you can also:</p>
        <ul>
          <li>Check our in-app help section</li>
          <li>Visit our knowledge base online</li>
          <li>Connect with our community forum</li>
          <li>Follow us on social media for tips and updates</li>
        </ul>
        
        <p><strong>Emergency Support:</strong> For urgent issues, please include "URGENT" in your email subject line.</p>
        <p><strong>Feature Requests:</strong> We love hearing your ideas! Send us your suggestions for new features.</p>
      </Section>

      <Footer>
        <p>&copy; 2025 Fitness Solutions. All rights reserved.</p>
        <div>
          <Link href="/beestmodeprivacyen">Privacy Policy</Link>
          <Link href="/beestmode/delete-account">Delete Account</Link>
          <Link href="#">Terms of Service</Link>
          <Link href="#">License Agreement</Link>
          <Link href="#">App Store</Link>
        </div>
      </Footer>
    </Container>
  );
};

export default BeestmodeSupportPageEN;
